import { useState, useMemo, useId, useEffect } from 'react';
import { useSearchParams } from 'react-router-dom';
import { PrintBillButton } from '@/components/ui/PrintBillButton';
import { PaymentTypeBadge } from '@/components/ui/PaymentTypeBadge';
import type { PaymentType } from '@/services/api/frontdesk.api';
import { BookingDetailSlideOver } from '@/components/frontdesk/BookingDetailSlideOver';
import {
  CalendarDays,
  List,
  Plus,
  X,
  ChevronDown,
  Loader2,
  Search,
  LogIn,
  LogOut,
  XCircle,
  Download,
} from 'lucide-react';
import { format, differenceInDays, parseISO } from 'date-fns';
import { BookingSource, BookingStatus } from '@pms/shared';
import { cn } from '@/lib/utils';
import { extractErrorMessage } from '@/lib/error';
import { ReservationCalendar } from '@/components/calendar/ReservationCalendar';
import {
  useBookings,
  useCreateBooking,
  useCancelBooking,
  useCheckInBooking,
  useCheckOutBooking,
} from '@/hooks/api/useBookings';
import { useUnits } from '@/hooks/api/useProperties';
import { useCancellationReasons } from '@/hooks/api/useCancellationReasons';
import { useSelectedPropertyId } from '@/contexts/PropertyContext';
import { formatMoney } from '@/lib/money';
import { toCsv, downloadCsv } from '@/lib/csv';

// ─── Constants ────────────────────────────────────────────────────────────────

type ViewMode = 'calendar' | 'list';

const SOURCE_OPTIONS = [
  { value: BookingSource.DIRECT, label: 'Direct' },
  { value: BookingSource.AIRBNB, label: 'Airbnb' },
  { value: BookingSource.BOOKING_COM, label: 'Booking.com' },
  { value: BookingSource.EXPEDIA, label: 'Expedia' },
  { value: BookingSource.AXIS_ROOMS, label: 'AxisRooms (OTA)' },
  { value: BookingSource.MANUAL, label: 'Manual' },
];

const SOURCE_LABELS: Record<string, string> = {
  [BookingSource.AIRBNB]: 'Airbnb',
  [BookingSource.BOOKING_COM]: 'Booking.com',
  [BookingSource.EXPEDIA]: 'Expedia',
  [BookingSource.AXIS_ROOMS]: 'AxisRooms',
  [BookingSource.DIRECT]: 'Direct',
  [BookingSource.MANUAL]: 'Manual',
};

const SOURCE_BADGE_COLORS: Record<string, string> = {
  [BookingSource.AIRBNB]: 'bg-rose-100 text-rose-700',
  [BookingSource.BOOKING_COM]: 'bg-blue-100 text-blue-700',
  [BookingSource.EXPEDIA]: 'bg-yellow-100 text-yellow-700',
  [BookingSource.AXIS_ROOMS]: 'bg-indigo-100 text-indigo-700',
  [BookingSource.DIRECT]: 'bg-emerald-100 text-emerald-700',
  [BookingSource.MANUAL]: 'bg-gray-100 text-gray-600',
};

const STATUS_STYLES: Record<string, string> = {
  [BookingStatus.CONFIRMED]: 'bg-green-100 text-green-800',
  [BookingStatus.PENDING]: 'bg-yellow-100 text-yellow-800',
  [BookingStatus.CHECKED_IN]: 'bg-blue-100 text-blue-800',
  [BookingStatus.CHECKED_OUT]: 'bg-gray-100 text-gray-600',
  [BookingStatus.CANCELLED]: 'bg-red-100 text-red-700',
  [BookingStatus.NO_SHOW]: 'bg-orange-100 text-orange-700',
};

const STATUS_LABELS: Record<string, string> = {
  [BookingStatus.CONFIRMED]: 'Confirmed',
  [BookingStatus.PENDING]: 'Pending',
  [BookingStatus.CHECKED_IN]: 'Checked In',
  [BookingStatus.CHECKED_OUT]: 'Checked Out',
  [BookingStatus.CANCELLED]: 'Cancelled',
  [BookingStatus.NO_SHOW]: 'No Show',
};

const STATUS_FILTER_OPTIONS = [
  { value: '', label: 'All Statuses' },
  { value: BookingStatus.CONFIRMED, label: 'Confirmed' },
  { value: BookingStatus.PENDING, label: 'Pending' },
  { value: BookingStatus.CHECKED_IN, label: 'Checked In' },
  { value: BookingStatus.CHECKED_OUT, label: 'Checked Out' },
  { value: BookingStatus.CANCELLED, label: 'Cancelled' },
];

// ─── Helpers ──────────────────────────────────────────────────────────────────

function fmtDate(iso: string): string {
  try {
    return format(parseISO(iso), 'MMM d, yyyy');
  } catch {
    return iso;
  }
}

function nightsCount(checkIn: string, checkOut: string): number {
  try {
    return differenceInDays(parseISO(checkOut), parseISO(checkIn));
  } catch {
    return 0;
  }
}

// ─── New Booking Slide-over ───────────────────────────────────────────────────

interface NewBookingFormData {
  unitId: string;
  checkIn: string;
  checkOut: string;
  guestName: string;
  guestEmail: string;
  guestPhone: string;
  guestsCount: string;
  source: string;
  specialRequests: string;
  promoCode: string;
}

const EMPTY_BOOKING_FORM: NewBookingFormData = {
  unitId: '',
  checkIn: '',
  checkOut: '',
  guestName: '',
  guestEmail: '',
  guestPhone: '',
  guestsCount: '1',
  source: BookingSource.DIRECT,
  specialRequests: '',
  promoCode: '',
};

function NewBookingSlideOver({
  isOpen,
  onClose,
  units,
}: {
  isOpen: boolean;
  onClose: () => void;
  units: Array<{ id: string; name: string; propertyName: string; maxGuests: number }>;
}) {
  const headingId = useId();
  const [form, setForm] = useState<NewBookingFormData>({
    ...EMPTY_BOOKING_FORM,
    unitId: units[0]?.id ?? '',
  });
  const [errors, setErrors] = useState<Partial<Record<keyof NewBookingFormData, string>>>({});
  // TASK-68: summary of the just-created booking (discount + deposit-due lines).
  const [createdBooking, setCreatedBooking] = useState<Record<string, unknown> | null>(null);

  const createMutation = useCreateBooking();

  function set<K extends keyof NewBookingFormData>(key: K, value: string) {
    setForm((prev) => ({ ...prev, [key]: value }));
    if (errors[key]) setErrors((prev) => ({ ...prev, [key]: undefined }));
    if (createMutation.isError) createMutation.reset();
    if (createdBooking) setCreatedBooking(null);
  }

  function validate(): boolean {
    const next: Partial<Record<keyof NewBookingFormData, string>> = {};
    if (!form.unitId) next.unitId = 'Unit is required';
    if (!form.checkIn) next.checkIn = 'Check-in date is required';
    if (!form.checkOut) next.checkOut = 'Check-out date is required';
    if (form.checkIn && form.checkOut && form.checkIn >= form.checkOut) {
      next.checkOut = 'Check-out must be after check-in';
    }
    if (!form.guestName.trim()) next.guestName = 'Guest name is required';
    if (!form.guestEmail.trim()) next.guestEmail = 'Guest email is required';
    setErrors(next);
    return Object.keys(next).length === 0;
  }

  function handleSubmit(e: React.FormEvent) {
    e.preventDefault();
    if (!validate()) return;
    createMutation.mutate(
      {
        unitId: form.unitId,
        checkIn: form.checkIn,
        checkOut: form.checkOut,
        guestsCount: parseInt(form.guestsCount, 10) || 1,
        source: form.source,
        guestData: {
          name: form.guestName.trim(),
          ...(form.guestEmail.trim() ? { email: form.guestEmail.trim() } : {}),
          ...(form.guestPhone.trim() ? { phone: form.guestPhone.trim() } : {}),
        },
        ...(form.specialRequests.trim() ? { specialRequests: form.specialRequests.trim() } : {}),
        ...(form.promoCode.trim() ? { promoCode: form.promoCode.trim() } : {}),
      },
      {
        onSuccess: (booking) => {
          // Keep the slide-over open to surface the discount + deposit-due
          // summary returned on the created booking (TASK-68 Flows 2 & 4).
          setCreatedBooking(
            booking && typeof booking === 'object'
              ? (booking as Record<string, unknown>)
              : null,
          );
          setForm({ ...EMPTY_BOOKING_FORM, unitId: units[0]?.id ?? '' });
          setErrors({});
        },
      },
    );
  }

  function handleClose() {
    setForm({ ...EMPTY_BOOKING_FORM, unitId: units[0]?.id ?? '' });
    setErrors({});
    setCreatedBooking(null);
    onClose();
  }

  if (!isOpen) return null;

  const nights =
    form.checkIn && form.checkOut ? nightsCount(form.checkIn, form.checkOut) : 0;

  return (
    <>
      <div
        className="fixed inset-0 bg-black/40 z-40"
        aria-hidden="true"
        onClick={handleClose}
      />
      <aside
        role="dialog"
        aria-modal="true"
        aria-labelledby={headingId}
        className="fixed right-0 top-0 h-full w-full max-w-md bg-white shadow-2xl z-50 flex flex-col"
      >
        {/* Header */}
        <div className="flex items-center justify-between px-5 py-4 border-b border-gray-200 shrink-0">
          <h2 id={headingId} className="text-base font-semibold text-gray-900">
            New Booking
          </h2>
          <button
            type="button"
            onClick={handleClose}
            aria-label="Close"
            className="p-1.5 rounded-lg text-gray-400 hover:text-gray-600 hover:bg-gray-100 focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-primary-500 transition-colors"
          >
            <X size={18} aria-hidden="true" />
          </button>
        </div>

        {/* Form */}
        <form onSubmit={handleSubmit} className="flex-1 overflow-y-auto p-5 space-y-4">
          {/* Unit */}
          <div>
            <label className="block text-xs font-semibold text-gray-500 uppercase tracking-wide mb-1.5">
              Unit <span aria-label="required">*</span>
            </label>
            <div className="relative">
              <select
                value={form.unitId}
                onChange={(e) => set('unitId', e.target.value)}
                required
                className={cn(
                  'w-full appearance-none rounded-lg border px-3 py-2 pr-8 text-sm text-gray-800 bg-white focus:outline-none focus:ring-2 focus:ring-primary-500 focus:border-primary-500',
                  errors.unitId ? 'border-red-400' : 'border-gray-300',
                )}
              >
                <option value="">Select a unit...</option>
                {units.map((u) => (
                  <option key={u.id} value={u.id}>
                    {u.propertyName} — {u.name}
                  </option>
                ))}
              </select>
              <ChevronDown
                size={14}
                className="absolute right-2.5 top-1/2 -translate-y-1/2 text-gray-400 pointer-events-none"
                aria-hidden="true"
              />
            </div>
            {errors.unitId && (
              <p className="mt-1 text-xs text-red-600">{errors.unitId}</p>
            )}
          </div>

          {/* Date range */}
          <div className="grid grid-cols-2 gap-3">
            <div>
              <label className="block text-xs font-semibold text-gray-500 uppercase tracking-wide mb-1.5">
                Check-in <span aria-label="required">*</span>
              </label>
              <input
                type="date"
                value={form.checkIn}
                onChange={(e) => set('checkIn', e.target.value)}
                required
                className={cn(
                  'w-full rounded-lg border px-3 py-2 text-sm text-gray-800 focus:outline-none focus:ring-2 focus:ring-primary-500 focus:border-primary-500',
                  errors.checkIn ? 'border-red-400' : 'border-gray-300',
                )}
              />
              {errors.checkIn && (
                <p className="mt-1 text-xs text-red-600">{errors.checkIn}</p>
              )}
            </div>
            <div>
              <label className="block text-xs font-semibold text-gray-500 uppercase tracking-wide mb-1.5">
                Check-out <span aria-label="required">*</span>
              </label>
              <input
                type="date"
                value={form.checkOut}
                onChange={(e) => set('checkOut', e.target.value)}
                required
                min={form.checkIn || undefined}
                className={cn(
                  'w-full rounded-lg border px-3 py-2 text-sm text-gray-800 focus:outline-none focus:ring-2 focus:ring-primary-500 focus:border-primary-500',
                  errors.checkOut ? 'border-red-400' : 'border-gray-300',
                )}
              />
              {errors.checkOut && (
                <p className="mt-1 text-xs text-red-600">{errors.checkOut}</p>
              )}
            </div>
          </div>
          {nights > 0 && (
            <p className="text-xs text-primary-600 font-medium -mt-1">
              {nights} night{nights !== 1 ? 's' : ''}
            </p>
          )}

          {/* Guest info */}
          <div className="pt-1 pb-0.5">
            <p className="text-xs font-semibold text-gray-400 uppercase tracking-wide mb-3">
              Guest Information
            </p>
            <div className="space-y-3">
              <div>
                <label className="block text-xs font-semibold text-gray-500 uppercase tracking-wide mb-1.5">
                  Full Name <span aria-label="required">*</span>
                </label>
                <input
                  type="text"
                  value={form.guestName}
                  onChange={(e) => set('guestName', e.target.value)}
                  placeholder="John Doe"
                  required
                  className={cn(
                    'w-full rounded-lg border px-3 py-2 text-sm text-gray-800 placeholder:text-gray-400 focus:outline-none focus:ring-2 focus:ring-primary-500 focus:border-primary-500',
                    errors.guestName ? 'border-red-400' : 'border-gray-300',
                  )}
                />
                {errors.guestName && (
                  <p className="mt-1 text-xs text-red-600">{errors.guestName}</p>
                )}
              </div>
              <div>
                <label className="block text-xs font-semibold text-gray-500 uppercase tracking-wide mb-1.5">
                  Email <span aria-label="required">*</span>
                </label>
                <input
                  type="email"
                  value={form.guestEmail}
                  onChange={(e) => set('guestEmail', e.target.value)}
                  placeholder="john@example.com"
                  required
                  className={cn(
                    'w-full rounded-lg border px-3 py-2 text-sm text-gray-800 placeholder:text-gray-400 focus:outline-none focus:ring-2 focus:ring-primary-500 focus:border-primary-500',
                    errors.guestEmail ? 'border-red-400' : 'border-gray-300',
                  )}
                />
                {errors.guestEmail && (
                  <p className="mt-1 text-xs text-red-600">{errors.guestEmail}</p>
                )}
              </div>
              <div>
                <label className="block text-xs font-semibold text-gray-500 uppercase tracking-wide mb-1.5">
                  Phone
                </label>
                <input
                  type="tel"
                  value={form.guestPhone}
                  onChange={(e) => set('guestPhone', e.target.value)}
                  placeholder="+1 (555) 000-0000"
                  className="w-full rounded-lg border border-gray-300 px-3 py-2 text-sm text-gray-800 placeholder:text-gray-400 focus:outline-none focus:ring-2 focus:ring-primary-500 focus:border-primary-500"
                />
              </div>
            </div>
          </div>

          {/* Booking details */}
          <div className="grid grid-cols-2 gap-3">
            <div>
              <label className="block text-xs font-semibold text-gray-500 uppercase tracking-wide mb-1.5">
                Guests
              </label>
              <input
                type="number"
                min="1"
                max="20"
                value={form.guestsCount}
                onChange={(e) => set('guestsCount', e.target.value)}
                className="w-full rounded-lg border border-gray-300 px-3 py-2 text-sm text-gray-800 focus:outline-none focus:ring-2 focus:ring-primary-500 focus:border-primary-500"
              />
            </div>
            <div>
              <label className="block text-xs font-semibold text-gray-500 uppercase tracking-wide mb-1.5">
                Source
              </label>
              <div className="relative">
                <select
                  value={form.source}
                  onChange={(e) => set('source', e.target.value)}
                  className="w-full appearance-none rounded-lg border border-gray-300 px-3 py-2 pr-8 text-sm text-gray-800 bg-white focus:outline-none focus:ring-2 focus:ring-primary-500 focus:border-primary-500"
                >
                  {SOURCE_OPTIONS.map((opt) => (
                    <option key={opt.value} value={opt.value}>
                      {opt.label}
                    </option>
                  ))}
                </select>
                <ChevronDown
                  size={14}
                  className="absolute right-2.5 top-1/2 -translate-y-1/2 text-gray-400 pointer-events-none"
                  aria-hidden="true"
                />
              </div>
            </div>
          </div>

          {/* Special requests */}
          <div>
            <label className="block text-xs font-semibold text-gray-500 uppercase tracking-wide mb-1.5">
              Special Requests
            </label>
            <textarea
              rows={3}
              value={form.specialRequests}
              onChange={(e) => set('specialRequests', e.target.value)}
              placeholder="Early check-in, extra towels, etc."
              className="w-full rounded-lg border border-gray-300 px-3 py-2 text-sm text-gray-800 placeholder:text-gray-400 focus:outline-none focus:ring-2 focus:ring-primary-500 focus:border-primary-500 resize-none"
            />
          </div>

          {/* Promo code (TASK-68 Flow 2) — optional; absent ⇒ pricing unchanged */}
          <div>
            <label className="block text-xs font-semibold text-gray-500 uppercase tracking-wide mb-1.5">
              Promo Code
            </label>
            <input
              type="text"
              value={form.promoCode}
              onChange={(e) => set('promoCode', e.target.value.toUpperCase())}
              placeholder="e.g. SUMMER25"
              autoCapitalize="characters"
              className="w-full rounded-lg border border-gray-300 px-3 py-2 text-sm font-mono uppercase tracking-wide text-gray-800 placeholder:text-gray-400 placeholder:normal-case focus:outline-none focus:ring-2 focus:ring-primary-500 focus:border-primary-500"
            />
            <p className="mt-1 text-xs text-gray-400">
              Applied at booking; discount appears in the summary after creation.
            </p>
          </div>

          {/* Created-booking summary: discount + deposit-due (TASK-68 Flows 2 & 4) */}
          {createdBooking && (
            <div
              className="rounded-lg border border-emerald-200 bg-emerald-50 px-3 py-3 space-y-1.5"
              role="status"
            >
              <p className="text-sm font-semibold text-emerald-800">
                Booking created
              </p>
              {(() => {
                const discount = createdBooking.discountAmount as string | null | undefined;
                const total = (createdBooking.totalAmount as string | undefined) ?? undefined;
                const depositAmount = createdBooking.depositDueAmount as string | null | undefined;
                const depositDate = createdBooking.depositDueDate as string | null | undefined;
                const hasDiscount = discount != null && parseFloat(discount) > 0;
                const hasDeposit = depositAmount != null && parseFloat(depositAmount) > 0;
                return (
                  <>
                    {hasDiscount && (
                      <div className="flex items-center justify-between text-xs text-emerald-700">
                        <span>Discount applied</span>
                        <span className="font-semibold">
                          −{formatMoney(discount)}
                        </span>
                      </div>
                    )}
                    {total !== undefined && (
                      <div className="flex items-center justify-between text-xs text-gray-700">
                        <span>Total</span>
                        <span className="font-semibold">{formatMoney(total)}</span>
                      </div>
                    )}
                    {hasDeposit && (
                      <div className="flex items-center justify-between text-xs text-gray-700">
                        <span>
                          Deposit due
                          {depositDate ? ` (by ${fmtDate(depositDate)})` : ''}
                        </span>
                        <span className="font-semibold">
                          {formatMoney(depositAmount)}
                        </span>
                      </div>
                    )}
                  </>
                );
              })()}
              <button
                type="button"
                onClick={handleClose}
                className="mt-1 w-full px-3 py-1.5 rounded-md bg-emerald-600 text-white text-xs font-semibold hover:bg-emerald-700 focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-emerald-500"
              >
                Done
              </button>
            </div>
          )}

          {createMutation.isError && (
            <p className="text-sm text-red-600 bg-red-50 border border-red-200 rounded-lg px-3 py-2" role="alert">
              {extractErrorMessage(createMutation.error)}
            </p>
          )}

          <div className="pt-2">
            <button
              type="submit"
              disabled={createMutation.isPending}
              className={cn(
                'w-full px-4 py-2.5 rounded-lg text-white text-sm font-semibold transition-colors',
                'focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-primary-500',
                createMutation.isPending
                  ? 'bg-primary-400 cursor-not-allowed'
                  : 'bg-primary-600 hover:bg-primary-700',
              )}
            >
              {createMutation.isPending ? (
                <span className="flex items-center justify-center gap-2">
                  <Loader2 size={14} className="animate-spin" aria-hidden="true" />
                  Creating Booking...
                </span>
              ) : (
                'Create Booking'
              )}
            </button>
          </div>
        </form>
      </aside>
    </>
  );
}

// ─── Cancel Booking Dialog (TASK-68 Flow 3) ────────────────────────────────────

interface CancelTarget {
  id: string;
  guestName: string;
}

function CancelBookingDialog({
  target,
  onClose,
}: {
  target: CancelTarget | null;
  onClose: () => void;
}) {
  const headingId = useId();
  const propertyId = useSelectedPropertyId();
  const cancelMutation = useCancelBooking();

  const [reasonCodeId, setReasonCodeId] = useState('');
  const [note, setNote] = useState('');
  const [noteError, setNoteError] = useState<string | null>(null);

  // Active reason codes for the selected property; empty ⇒ free-text fallback.
  const { data: reasonsData } = useCancellationReasons(
    { propertyId, isActive: true },
    !!target && !!propertyId,
  );
  const reasons = useMemo(() => {
    const raw = reasonsData?.data ?? [];
    return raw.filter((r) => r.isActive);
  }, [reasonsData]);

  const selectedReason = useMemo(
    () => reasons.find((r) => r.id === reasonCodeId) ?? null,
    [reasons, reasonCodeId],
  );
  const noteRequired = selectedReason?.requiresNote ?? false;

  // Reset local state whenever the dialog target changes.
  useEffect(() => {
    setReasonCodeId('');
    setNote('');
    setNoteError(null);
    cancelMutation.reset();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [target?.id]);

  if (!target) return null;

  function handleConfirm() {
    if (!target) return;
    if (noteRequired && !note.trim()) {
      setNoteError('A note is required for this reason.');
      return;
    }
    cancelMutation.mutate(
      {
        id: target.id,
        ...(reasonCodeId ? { reasonCodeId } : {}),
        // When no code chosen, preserve the existing free-text path.
        ...(note.trim()
          ? { reason: note.trim() }
          : reasonCodeId
            ? {}
            : { reason: 'Cancelled by staff' }),
      },
      { onSuccess: onClose },
    );
  }

  return (
    <>
      <div
        className="fixed inset-0 bg-black/40 z-40"
        aria-hidden="true"
        onClick={onClose}
      />
      <div
        role="dialog"
        aria-modal="true"
        aria-labelledby={headingId}
        className="fixed left-1/2 top-1/2 -translate-x-1/2 -translate-y-1/2 w-full max-w-md bg-white rounded-xl shadow-2xl z-50 p-5"
      >
        <div className="flex items-center justify-between mb-3">
          <h2 id={headingId} className="text-base font-semibold text-gray-900">
            Cancel Booking
          </h2>
          <button
            type="button"
            onClick={onClose}
            aria-label="Close"
            className="p-1.5 rounded-lg text-gray-400 hover:text-gray-600 hover:bg-gray-100 focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-primary-500"
          >
            <X size={18} aria-hidden="true" />
          </button>
        </div>

        <p className="text-sm text-gray-500 mb-4">
          Cancel the reservation for{' '}
          <span className="font-medium text-gray-800">{target.guestName}</span>?
        </p>

        {/* Reason select — only when active codes exist; else free-text below */}
        {reasons.length > 0 && (
          <div className="mb-3">
            <label className="block text-xs font-semibold text-gray-500 uppercase tracking-wide mb-1.5">
              Reason
            </label>
            <div className="relative">
              <select
                value={reasonCodeId}
                onChange={(e) => {
                  setReasonCodeId(e.target.value);
                  setNoteError(null);
                }}
                className="w-full appearance-none rounded-lg border border-gray-300 bg-white px-3 py-2 pr-8 text-sm text-gray-800 focus:outline-none focus:ring-2 focus:ring-primary-500 focus:border-primary-500"
              >
                <option value="">Select a reason...</option>
                {reasons.map((r) => (
                  <option key={r.id} value={r.id}>
                    {r.label}
                  </option>
                ))}
              </select>
              <ChevronDown
                size={14}
                className="absolute right-2.5 top-1/2 -translate-y-1/2 text-gray-400 pointer-events-none"
                aria-hidden="true"
              />
            </div>
          </div>
        )}

        {/* Note — required when the chosen reason demands it, else optional */}
        <div className="mb-4">
          <label className="block text-xs font-semibold text-gray-500 uppercase tracking-wide mb-1.5">
            Note{' '}
            {noteRequired ? (
              <span aria-label="required">*</span>
            ) : (
              <span className="text-gray-400 normal-case font-normal">(optional)</span>
            )}
          </label>
          <textarea
            rows={3}
            value={note}
            onChange={(e) => {
              setNote(e.target.value);
              if (noteError) setNoteError(null);
            }}
            placeholder={
              reasons.length > 0
                ? 'Add a note for this cancellation...'
                : 'Reason for cancellation...'
            }
            className={cn(
              'w-full rounded-lg border px-3 py-2 text-sm text-gray-800 placeholder:text-gray-400 focus:outline-none focus:ring-2 focus:ring-primary-500 focus:border-primary-500 resize-none',
              noteError ? 'border-red-400' : 'border-gray-300',
            )}
          />
          {noteError && <p className="mt-1 text-xs text-red-600">{noteError}</p>}
        </div>

        {cancelMutation.isError && (
          <p
            className="mb-3 text-sm text-red-600 bg-red-50 border border-red-200 rounded-lg px-3 py-2"
            role="alert"
          >
            {extractErrorMessage(cancelMutation.error)}
          </p>
        )}

        <div className="flex justify-end gap-2">
          <button
            type="button"
            onClick={onClose}
            className="px-4 py-2 rounded-lg border border-gray-300 text-sm font-medium text-gray-600 hover:bg-gray-50 focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-primary-500"
          >
            Keep Booking
          </button>
          <button
            type="button"
            onClick={handleConfirm}
            disabled={cancelMutation.isPending}
            className={cn(
              'px-4 py-2 rounded-lg text-white text-sm font-semibold focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-red-400',
              cancelMutation.isPending
                ? 'bg-red-400 cursor-not-allowed'
                : 'bg-red-600 hover:bg-red-700',
            )}
          >
            {cancelMutation.isPending ? (
              <span className="flex items-center gap-2">
                <Loader2 size={14} className="animate-spin" aria-hidden="true" />
                Cancelling...
              </span>
            ) : (
              'Confirm Cancel'
            )}
          </button>
        </div>
      </div>
    </>
  );
}

// ─── Bookings List View ────────────────────────────────────────────────────────

function BookingsListView({ onOpenBooking }: { onOpenBooking: (id: string) => void }) {
  const [search, setSearch] = useState('');
  const [statusFilter, setStatusFilter] = useState('');
  const [page, setPage] = useState(1);

  const propertyId = useSelectedPropertyId();
  const checkInMutation = useCheckInBooking();
  const checkOutMutation = useCheckOutBooking();
  const [cancelTarget, setCancelTarget] = useState<CancelTarget | null>(null);

  const bookingsQuery = {
    ...(statusFilter ? { status: statusFilter } : {}),
    ...(propertyId ? { propertyId } : {}),
    page,
    limit: 20,
  };
  const { data, isLoading } = useBookings(bookingsQuery);

  const allItems: Record<string, unknown>[] = data
    ? (Array.isArray(data) ? data : (data.data ?? []))
    : [];

  const meta = data && !Array.isArray(data) ? data.meta : null;
  const totalPages = meta
    ? Math.ceil((meta.total ?? allItems.length) / (meta.limit ?? 20))
    : 1;

  // Client-side search filter on top of server results
  const items = useMemo(() => {
    if (!search.trim()) return allItems;
    const q = search.toLowerCase();
    return allItems.filter((bk) => {
      const guestName = ((bk.guest as Record<string, unknown>)?.fullName as string ?? (bk.guestName as string) ?? '').toLowerCase();
      const unitName = ((bk.unit as Record<string, unknown>)?.name as string ?? (bk.unitName as string) ?? '').toLowerCase();
      const propertyName = ((bk.property as Record<string, unknown>)?.name as string ?? (bk.propertyName as string) ?? '').toLowerCase();
      return guestName.includes(q) || unitName.includes(q) || propertyName.includes(q);
    });
  }, [allItems, search]);

  const statusSelectId = useId();

  function handleExportCsv() {
    const exportRows = items.map((bk) => {
      const guestName =
        ((bk.guest as Record<string, unknown>)?.fullName as string) ??
        (bk.guestName as string) ??
        'Guest';
      const unitName =
        ((bk.unit as Record<string, unknown>)?.name as string) ??
        (bk.unitName as string) ??
        '';
      const propertyName =
        (bk.propertyName as string) ??
        ((bk.property as Record<string, unknown>)?.name as string) ??
        '';
      const status = (bk.status as string) ?? '';
      const source = (bk.source as string) ?? '';
      const checkIn = (bk.checkIn as string) ?? '';
      const checkOut = (bk.checkOut as string) ?? '';
      const amount = (bk.totalAmount as string) ?? (bk.baseAmount as string) ?? '';
      const nights = nightsCount(checkIn, checkOut);
      return {
        guest: guestName,
        unit: unitName,
        property: propertyName,
        checkIn: checkIn ? fmtDate(checkIn) : '',
        checkOut: checkOut ? fmtDate(checkOut) : '',
        nights: nights > 0 ? nights : '',
        status: STATUS_LABELS[status] ?? status,
        source: SOURCE_LABELS[source] ?? source,
        amount,
        paymentType: (bk.paymentType as string) ?? '',
      };
    });
    const csv = toCsv(exportRows, [
      { key: 'guest', header: 'Guest' },
      { key: 'unit', header: 'Unit' },
      { key: 'property', header: 'Property' },
      { key: 'checkIn', header: 'Check-in' },
      { key: 'checkOut', header: 'Check-out' },
      { key: 'nights', header: 'Nights' },
      { key: 'status', header: 'Status' },
      { key: 'source', header: 'Source' },
      { key: 'amount', header: 'Amount' },
      { key: 'paymentType', header: 'Payment Type' },
    ]);
    downloadCsv(`reservations-${new Date().toISOString().slice(0, 10)}.csv`, csv);
  }

  return (
    <div className="space-y-4">
      {/* Filters */}
      <div className="flex flex-wrap items-center gap-3">
        <div className="relative flex-1 min-w-[200px] max-w-sm">
          <Search
            size={15}
            className="absolute left-3 top-1/2 -translate-y-1/2 text-gray-400 pointer-events-none"
            aria-hidden="true"
          />
          <input
            type="search"
            value={search}
            onChange={(e) => setSearch(e.target.value)}
            placeholder="Search guest, unit, property..."
            aria-label="Search bookings"
            className="w-full pl-9 pr-3 py-2 rounded-lg border border-gray-300 text-sm text-gray-800 bg-white placeholder:text-gray-400 focus:outline-none focus:ring-2 focus:ring-primary-500 focus:border-primary-500"
          />
        </div>

        <div className="relative">
          <label htmlFor={statusSelectId} className="sr-only">
            Filter by status
          </label>
          <select
            id={statusSelectId}
            value={statusFilter}
            onChange={(e) => {
              setStatusFilter(e.target.value);
              setPage(1);
            }}
            className="appearance-none rounded-lg border border-gray-300 bg-white px-3 py-2 pr-8 text-sm text-gray-800 focus:outline-none focus:ring-2 focus:ring-primary-500 focus:border-primary-500"
          >
            {STATUS_FILTER_OPTIONS.map((opt) => (
              <option key={opt.value} value={opt.value}>
                {opt.label}
              </option>
            ))}
          </select>
          <ChevronDown
            size={14}
            className="absolute right-2.5 top-1/2 -translate-y-1/2 text-gray-400 pointer-events-none"
            aria-hidden="true"
          />
        </div>

        <p className="text-xs text-gray-500 ml-auto">
          {items.length} booking{items.length !== 1 ? 's' : ''}
        </p>

        <button
          type="button"
          onClick={handleExportCsv}
          disabled={items.length === 0}
          className={cn(
            'inline-flex items-center gap-1.5 px-3 py-2 rounded-lg text-xs font-medium border transition-colors',
            'border-gray-300 text-gray-600 hover:bg-gray-50',
            'focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-primary-500',
            'disabled:opacity-50 disabled:cursor-not-allowed',
          )}
          aria-label="Export bookings as CSV"
        >
          <Download size={14} aria-hidden="true" />
          Export CSV
        </button>
      </div>

      {/* Table */}
      <div className="bg-white rounded-xl border border-gray-200 shadow-sm overflow-hidden">
        {isLoading ? (
          <div className="flex items-center justify-center py-16">
            <Loader2 size={24} className="animate-spin text-primary-600" aria-hidden="true" />
            <span className="ml-2 text-sm text-gray-500">Loading bookings...</span>
          </div>
        ) : (
          <div className="overflow-x-auto">
            <table className="min-w-full divide-y divide-gray-100" aria-label="Bookings list">
              <thead>
                <tr className="bg-gray-50">
                  {['Guest', 'Unit', 'Check-in', 'Check-out', 'Nights', 'Status', 'Source', 'Amount', 'Payment', 'Actions'].map(
                    (col) => (
                      <th
                        key={col}
                        scope="col"
                        className="px-4 py-3 text-left text-xs font-semibold text-gray-500 uppercase tracking-wide whitespace-nowrap"
                      >
                        {col}
                      </th>
                    ),
                  )}
                </tr>
              </thead>
              <tbody className="divide-y divide-gray-100">
                {items.length === 0 ? (
                  <tr>
                    <td colSpan={10} className="px-4 py-12 text-center text-sm text-gray-400">
                      No bookings found.
                    </td>
                  </tr>
                ) : (
                  items.map((bk) => {
                    const id = (bk.id as string) ?? '';
                    const guestName =
                      ((bk.guest as Record<string, unknown>)?.fullName as string) ??
                      (bk.guestName as string) ??
                      'Guest';
                    const unitName =
                      ((bk.unit as Record<string, unknown>)?.name as string) ??
                      (bk.unitName as string) ??
                      '';
                    const propertyName =
                      (bk.propertyName as string) ??
                      ((bk.property as Record<string, unknown>)?.name as string) ??
                      '';
                    const status = (bk.status as string) ?? '';
                    const source = (bk.source as string) ?? '';
                    const checkIn = (bk.checkIn as string) ?? '';
                    const checkOut = (bk.checkOut as string) ?? '';
                    const amount = (bk.totalAmount as string) ?? (bk.baseAmount as string) ?? '—';
                    const nights = nightsCount(checkIn, checkOut);

                    // A freshly-created booking is PENDING; the check-in endpoint
                    // accepts both 'confirmed' and 'pending' (same as Front Desk →
                    // Arrivals), so a manager can advance it straight from the list
                    // instead of dead-ending at Cancel.
                    const canCheckIn =
                      status === BookingStatus.CONFIRMED ||
                      status === BookingStatus.PENDING;
                    const canCheckOut = status === BookingStatus.CHECKED_IN;
                    const canCancel =
                      status !== BookingStatus.CANCELLED &&
                      status !== BookingStatus.CHECKED_OUT;
                    const canPrintBill =
                      status === BookingStatus.CHECKED_IN ||
                      status === BookingStatus.CHECKED_OUT;

                    return (
                      <tr key={id} className="hover:bg-gray-50 transition-colors">
                        <td
                          className="px-4 py-3.5 cursor-pointer"
                          onClick={() => id && onOpenBooking(id)}
                          role="button"
                          tabIndex={0}
                          onKeyDown={(e) => {
                            if ((e.key === 'Enter' || e.key === ' ') && id) {
                              e.preventDefault();
                              onOpenBooking(id);
                            }
                          }}
                          aria-label={`View reservation detail for ${guestName}`}
                        >
                          <div className="flex items-center gap-2">
                            <div
                              className="flex items-center justify-center w-7 h-7 rounded-full bg-primary-100 text-primary-700 text-xs font-bold shrink-0"
                              aria-hidden="true"
                            >
                              {guestName.slice(0, 2).toUpperCase()}
                            </div>
                            <div>
                              <p className="text-sm font-medium text-gray-900 whitespace-nowrap">
                                {guestName}
                              </p>
                              {propertyName && (
                                <p className="text-xs text-gray-400 whitespace-nowrap">
                                  {propertyName}
                                </p>
                              )}
                            </div>
                          </div>
                        </td>
                        <td className="px-4 py-3.5 text-sm text-gray-700 whitespace-nowrap">
                          {unitName}
                        </td>
                        <td className="px-4 py-3.5 text-sm text-gray-600 whitespace-nowrap">
                          {fmtDate(checkIn)}
                        </td>
                        <td className="px-4 py-3.5 text-sm text-gray-600 whitespace-nowrap">
                          {fmtDate(checkOut)}
                        </td>
                        <td className="px-4 py-3.5 text-sm text-gray-600 text-center">
                          {nights > 0 ? nights : '—'}
                        </td>
                        <td className="px-4 py-3.5 whitespace-nowrap">
                          <span
                            className={cn(
                              'inline-flex items-center px-2 py-0.5 rounded-full text-xs font-medium',
                              STATUS_STYLES[status] ?? 'bg-gray-100 text-gray-600',
                            )}
                          >
                            {STATUS_LABELS[status] ?? status}
                          </span>
                        </td>
                        <td className="px-4 py-3.5 whitespace-nowrap">
                          <span
                            className={cn(
                              'inline-flex items-center px-2 py-0.5 rounded-full text-xs font-medium',
                              SOURCE_BADGE_COLORS[source] ?? 'bg-gray-100 text-gray-600',
                            )}
                          >
                            {SOURCE_LABELS[source] ?? source}
                          </span>
                        </td>
                        <td className="px-4 py-3.5 text-sm font-semibold text-gray-900 whitespace-nowrap">
                          {typeof amount === 'string' && !amount.startsWith('$')
                            ? `$${parseFloat(amount).toLocaleString()}`
                            : amount}
                        </td>
                        <td className="px-4 py-3.5 whitespace-nowrap">
                          {bk.paymentType ? (
                            <PaymentTypeBadge paymentType={bk.paymentType as PaymentType} />
                          ) : (
                            <span className="text-xs text-gray-400">—</span>
                          )}
                        </td>
                        <td className="px-4 py-3.5">
                          <div className="flex items-center gap-1">
                            {canCheckIn && (
                              <button
                                type="button"
                                onClick={() => checkInMutation.mutate({ id })}
                                disabled={checkInMutation.isPending}
                                aria-label={`Check in ${guestName}`}
                                title="Check In"
                                className="inline-flex items-center gap-1 px-2 py-1 rounded-md bg-primary-50 text-primary-700 text-xs font-medium hover:bg-primary-100 focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-primary-500 transition-colors"
                              >
                                <LogIn size={12} aria-hidden="true" />
                                Check In
                              </button>
                            )}
                            {canCheckOut && (
                              <button
                                type="button"
                                onClick={() => checkOutMutation.mutate({ id })}
                                disabled={checkOutMutation.isPending}
                                aria-label={`Check out ${guestName}`}
                                title="Check Out"
                                className="inline-flex items-center gap-1 px-2 py-1 rounded-md bg-amber-50 text-amber-700 text-xs font-medium hover:bg-amber-100 focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-amber-500 transition-colors"
                              >
                                <LogOut size={12} aria-hidden="true" />
                                Check Out
                              </button>
                            )}
                            {canPrintBill && (
                              <PrintBillButton
                                bookingId={id}
                                variant="icon"
                              />
                            )}
                            {canCancel && (
                              <button
                                type="button"
                                onClick={() => setCancelTarget({ id, guestName })}
                                aria-label={`Cancel booking for ${guestName}`}
                                title="Cancel"
                                className="p-1.5 rounded-md text-gray-400 hover:text-red-600 hover:bg-red-50 focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-red-400 transition-colors"
                              >
                                <XCircle size={14} aria-hidden="true" />
                              </button>
                            )}
                          </div>
                        </td>
                      </tr>
                    );
                  })
                )}
              </tbody>
            </table>
          </div>
        )}

        {/* Pagination */}
        {totalPages > 1 && (
          <div className="flex items-center justify-between px-4 py-3 border-t border-gray-100">
            <p className="text-xs text-gray-500">Page {page} of {totalPages}</p>
            <div className="flex gap-2">
              <button
                onClick={() => setPage((p) => Math.max(1, p - 1))}
                disabled={page <= 1}
                className={cn(
                  'px-3 py-1.5 rounded-lg text-xs font-medium border transition-colors',
                  'focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-primary-500',
                  page <= 1
                    ? 'border-gray-200 text-gray-300 cursor-not-allowed'
                    : 'border-gray-300 text-gray-600 hover:bg-gray-50',
                )}
              >
                Previous
              </button>
              <button
                onClick={() => setPage((p) => Math.min(totalPages, p + 1))}
                disabled={page >= totalPages}
                className={cn(
                  'px-3 py-1.5 rounded-lg text-xs font-medium border transition-colors',
                  'focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-primary-500',
                  page >= totalPages
                    ? 'border-gray-200 text-gray-300 cursor-not-allowed'
                    : 'border-gray-300 text-gray-600 hover:bg-gray-50',
                )}
              >
                Next
              </button>
            </div>
          </div>
        )}
      </div>

      {/* Cancel dialog with reason-code select (TASK-68 Flow 3) */}
      <CancelBookingDialog
        target={cancelTarget}
        onClose={() => setCancelTarget(null)}
      />
    </div>
  );
}

// ─── Page ─────────────────────────────────────────────────────────────────────

export function ReservationsPage() {
  // Default to list view so booking action buttons (Check In, Check Out, Print Bill)
  // are immediately visible. Staff can switch to calendar for the timeline view.
  const [view, setView] = useState<ViewMode>('list');
  const [isNewBookingOpen, setIsNewBookingOpen] = useState(false);
  const [selectedBookingId, setSelectedBookingId] = useState<string | null>(null);

  const [searchParams, setSearchParams] = useSearchParams();
  const openParam = searchParams.get('open');

  // Recently-Viewed re-entry: ?open=<bookingId> pre-opens the detail panel.
  useEffect(() => {
    if (openParam) setSelectedBookingId(openParam);
  }, [openParam]);

  function closeDetail() {
    setSelectedBookingId(null);
    if (searchParams.has('open')) {
      const next = new URLSearchParams(searchParams);
      next.delete('open');
      setSearchParams(next, { replace: true });
    }
  }

  const propertyId = useSelectedPropertyId();
  const { data: unitsData } = useUnits(propertyId || undefined);

  // Units from selected property only
  const units = useMemo(() => {
    const raw: Record<string, unknown>[] = Array.isArray(unitsData) ? unitsData : [];
    return raw.map((u) => ({
      id: (u.id as string) ?? '',
      name: (u.name as string) ?? '',
      propertyName: '',
      maxGuests: (u.capacity as number) ?? 2,
    }));
  }, [unitsData]);

  return (
    <div className="flex flex-col h-full space-y-4">
      {/* Page header */}
      <div className="flex items-center justify-between flex-wrap gap-3 shrink-0 mb-6">
        <div>
          <h1 className="text-xl font-semibold text-gray-900">Reservations</h1>
          <p className="text-sm text-gray-500 mt-0.5">
            {view === 'calendar'
              ? 'Timeline view of all units and bookings'
              : 'All bookings in a sortable list'}
          </p>
        </div>

        <div className="flex items-center gap-3">
          {/* View toggle — pill style */}
          <div
            className="inline-flex bg-gray-100 p-1 rounded-lg"
            role="group"
            aria-label="Switch view"
          >
            <button
              type="button"
              onClick={() => setView('calendar')}
              aria-pressed={view === 'calendar'}
              className={cn(
                'flex items-center gap-1.5 px-3 py-1.5 text-xs font-medium rounded-md transition-colors',
                'focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-primary-500 focus-visible:z-10',
                view === 'calendar'
                  ? 'bg-white shadow-sm text-gray-900'
                  : 'text-gray-500 hover:text-gray-700',
              )}
            >
              <CalendarDays size={14} aria-hidden="true" />
              Calendar
            </button>
            <button
              type="button"
              onClick={() => setView('list')}
              aria-pressed={view === 'list'}
              className={cn(
                'flex items-center gap-1.5 px-3 py-1.5 text-xs font-medium rounded-md transition-colors',
                'focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-primary-500 focus-visible:z-10',
                view === 'list'
                  ? 'bg-white shadow-sm text-gray-900'
                  : 'text-gray-500 hover:text-gray-700',
              )}
            >
              <List size={14} aria-hidden="true" />
              List
            </button>
          </div>

          {/* New Booking button */}
          <button
            type="button"
            onClick={() => setIsNewBookingOpen(true)}
            className="inline-flex items-center gap-2 px-4 py-2 bg-primary-600 hover:bg-primary-700 text-white text-sm font-medium rounded-lg transition-colors focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-primary-500"
          >
            <Plus size={16} aria-hidden="true" />
            New Booking
          </button>
        </div>
      </div>

      {/* Content */}
      {view === 'calendar' ? (
        <div className="flex-1 min-h-0">
          <ReservationCalendar />
        </div>
      ) : (
        <BookingsListView onOpenBooking={setSelectedBookingId} />
      )}

      {/* New Booking Slide-over */}
      <NewBookingSlideOver
        isOpen={isNewBookingOpen}
        onClose={() => setIsNewBookingOpen(false)}
        units={units}
      />

      {/* Booking detail (drives Recently Viewed + ?open= re-entry) */}
      <BookingDetailSlideOver bookingId={selectedBookingId} onClose={closeDetail} />
    </div>
  );
}
